import React from 'react';
import { Box, Typography, Divider } from '@mui/material';
import { useTranslation } from 'react-i18next';

// ============================================================================
// Types
// ============================================================================

export interface ConsumptionTotalsFooterProps {
  /** Total quantity across all items */
  totalQuantity: number;
  /** Total availed quantity */
  totalAvailed: number;
  /** Total pending quantity */
  totalPending: number;
  /** Total monetary value */
  totalValue: number;
}

// ============================================================================
// Sub-Component: Stat Item
// ============================================================================

interface StatItemProps {
  label: string;
  value: string | number;
  color?: string;
  highlight?: boolean;
}

const StatItem: React.FC<StatItemProps> = ({
  label,
  value,
  color = 'text.primary',
  highlight = false,
}) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1,
    }}
  >
    <Typography
      variant="body2"
      color="text.secondary"
      sx={{ fontWeight: 500 }}
    >
      {label}
    </Typography>
    <Typography
      variant="body2"
      sx={{
        fontWeight: 700,
        color,
        backgroundColor: highlight ? 'rgba(243, 122, 61, 0.08)' : 'transparent',
        px: highlight ? 1.5 : 0,
        py: highlight ? 0.5 : 0,
        borderRadius: highlight ? 1 : 0,
      }}
    >
      {value}
    </Typography>
  </Box>
);

// ============================================================================
// Main Component
// ============================================================================

const ConsumptionTotalsFooter: React.FC<ConsumptionTotalsFooterProps> = ({
  totalQuantity,
  totalAvailed,
  totalPending,
  totalValue,
}) => {
  const { t } = useTranslation('Packagebilling');

  return (
    <Box
      data-testid="consumption-totals-footer"
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        mt: 2,
        py: 1,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 3,
        }}
      >
        <StatItem
          label={t('consumption_details.total_quantity')}
          value={totalQuantity}
        />

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        <StatItem
          label={t('consumption_details.total_used')}
          value={totalAvailed}
          color="#1976d2"
        />

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        <StatItem
          label={t('consumption_details.total_pending')}
          value={totalPending}
          color="#f57c00"
        />

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        <StatItem
          label={t('consumption_details.total_value')}
          value={`${totalValue.toLocaleString()}`}
          color="#F37A3D"
          highlight
        />
      </Box>
    </Box>
  );
};

export default ConsumptionTotalsFooter;
